import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router';

import { Icon } from '../components/icons';
import { useProject } from '../projects/context';
import { type Block, internalPath, type Line, linkLabel, parseRichText } from './richTextRules';

// RichText: Text von Rückfragen, Aufgaben und Kommentaren mit Abschnitten, Listen, Fakten, Zitaten und Links
// (Regeln in richTextRules.ts). React maskiert jeden Text – HTML aus dem Text wird nie ausgeführt.
// compact: für Kommentare – Handlungsaufforderungen erscheinen dort als normaler Absatz.
export function RichText({ text, blocks, compact = false }: { text?: string | null; blocks?: Block[]; compact?: boolean }) {
  const { project } = useProject();
  const list = blocks ?? parseRichText(text);
  if (!list.length) return null;
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  const line = (l: Line) =>
    l.map((x, i) => {
      if (x.kind === 'strong') return <strong key={i}>{x.text}</strong>;
      if (x.kind === 'text') return <Fragment key={i}>{x.text}</Fragment>;
      const path = internalPath(x.href, project.key, origin);
      const label = x.auto ? linkLabel(path, x.href) : x.text;
      return path ? (
        <Link key={i} to={path}>
          {label}
        </Link>
      ) : (
        <a key={i} href={x.href} target="_blank" rel="noopener noreferrer">
          {label}
        </a>
      );
    });
  const lines = (ls: Line[]): ReactNode =>
    ls.map((l, i) => (
      <Fragment key={i}>
        {i > 0 && <br />}
        {line(l)}
      </Fragment>
    ));
  return (
    <div className={compact ? 'rich-text compact' : 'rich-text'}>
      {list.map((b, i) => {
        switch (b.kind) {
          case 'heading':
            return (
              <h3 key={i} className="rt-heading">
                {line(b.line)}
              </h3>
            );
          case 'facts':
            return (
              <dl key={i} className="rt-facts">
                {b.items.map((f, j) => (
                  <Fragment key={j}>
                    <dt>{f.label}</dt>
                    <dd>{line(f.value)}</dd>
                  </Fragment>
                ))}
              </dl>
            );
          case 'quote':
            return (
              <figure key={i} className="rt-quote">
                <figcaption>{b.label}</figcaption>
                <blockquote>{lines(b.lines)}</blockquote>
              </figure>
            );
          case 'list': {
            const items = b.items.map((item, j) => <li key={j}>{lines(item)}</li>);
            return b.ordered ? (
              <ol key={i} start={b.start}>
                {items}
              </ol>
            ) : (
              <ul key={i}>{items}</ul>
            );
          }
          case 'technical':
            return (
              <details key={i} className="rt-technical">
                <summary>Technische Angaben für Administratoren</summary>
                <pre>{b.lines.join('\n').trim()}</pre>
              </details>
            );
          case 'action':
            return compact ? (
              <p key={i}>{lines(b.lines)}</p>
            ) : (
              <p key={i} className="rt-action">
                <Icon name="check" size={16} />
                <span>{lines(b.lines)}</span>
              </p>
            );
          default:
            return (
              <p key={i} className={b.leadIn ? 'rt-lead-in' : undefined}>
                {lines(b.lines)}
              </p>
            );
        }
      })}
    </div>
  );
}
